import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalService } from '../../data-access/modal.service';
import { ModalComponent } from '../../ui/modal/modal.component';

/**
 * @description Container that reacts to modal state changes
 * by subscribing to the ModalService's observables. It controls the visibility of a modal
 * and passes down the necessary data to the `ModalComponent`.
 *
 * @see ModalComponent
 *
 * @example
 * ```
 * <!-- Include the modal wrapper in your component template where you want the modal to appear. -->
 * <app-modal-wrapper></app-modal-wrapper>
 *
 * // In your component TypeScript file, trigger the modal using the ModalService.
 * openModal() {
 *   this.modalService.openModal({
 *     text: 'Your modal content here',
 *     buttonText: 'Confirm',
 *     buttonAction: () => {
 *       // Action to perform on button click
 *     }
 *   });
 * }
 *
 * // To close the modal from your component, call the closeModal method of the ModalService.
 * closeModal() {
 *   this.modalService.closeModal();
 * }
 * ```
 */
@Component({
  selector: 'app-modal-wrapper',
  imports: [CommonModule, ModalComponent],
  templateUrl: './modal-wrapper.component.html',
})
export class ModalWrapperComponent implements OnInit, OnDestroy {
  public isModalOpen = false;
  public modalService = inject(ModalService);
  private subscriptions = new Subscription();

  ngOnInit() {
    this.subscriptions.add(
      this.modalService.openModalData$.subscribe(() => {
        this.isModalOpen = true;
      })
    );

    this.subscriptions.add(
      this.modalService.closeModalData$.subscribe(() => {
        this.isModalOpen = false;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
